import React, { useState } from 'react';
import { Link } from '@/components/LocaleLink';
import { cn } from '@/lib/utils';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { dataMaturityCopy } from '@/contexts/language/translations/pages/dataMaturity';
import { stairwayCopy } from '@/contexts/language/translations/pages/stairway';

const STEP_COLORS = ['#2A1E47', '#3D2E5C', '#4E3D6E', '#5F4D80', '#6F5D91', '#FF6B35'];

const DELIVER_ICONS = ['📊', '🏗️', '🎯', '⚡', '🧠', '🔄'];

const OurWorkData = () => {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  useScrollReveal();
  const { language } = useLanguage();
  const c = dataMaturityCopy[language];
  const stepNames = stairwayCopy[language].stepNames;
  const STEPS = c.steps.map((step, i) => ({
    level: i + 1,
    title: ('title' in step && step.title) || stepNames[i],
    subtitle: step.subtitle,
    description: step.description,
    symptoms: step.symptoms,
    color: STEP_COLORS[i],
  }));

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={c.seoTitle}
        description={c.seoDescription}
        canonical="/our-work/data"
      />
      <Navigation />
      <main className="index-page flex-1">

        {/* Hero */}
        <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-white">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FFF7EC] rounded-full filter blur-3xl opacity-60" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#E0E7FF] rounded-full filter blur-3xl opacity-40" />

          <div className="container-custom relative z-10 reveal">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#2A1E47] mb-6 leading-[1.08]">
                {c.hero.title}
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto mb-10">
                {c.hero.note}
              </p>
              <Link
                to="/tell-me-more"
                className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors"
              >
                {c.hero.cta}
              </Link>
              <p className="text-lg md:text-xl text-[#2A1E47] max-w-2xl mx-auto mt-8">
                {c.hero.subtitle}
              </p>
            </div>
          </div>
        </section>

        {/* Mission bar */}
        <section className="py-6 bg-[#352557]">
          <div className="container-custom">
            <p className="text-center text-white/90 text-lg md:text-xl font-medium tracking-wide">
              {c.mission}
            </p>
          </div>
        </section>

        {/* Visual stairway */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2A1E47] mb-4">
              {c.stairway.title}
            </h2>
            <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto mb-16">
              {c.stairway.subtitle}
            </p>

            {/* Stairway chart */}
            {(() => {
              const barBase = 80;
              const barStep = 52;
              const totalH = barBase + 6 * barStep; // tallest bar = level 6  (392)
              const svgPad = 130; // generous space above tallest bar for arrow + logo

              return (
                <div className="relative max-w-4xl mx-auto px-4">
                  {/* Logo above arrow tip — pushed up via negative margin */}
                  <div className="flex justify-end pr-2 -mb-6 relative z-40">
                    <img
                      src="/lovable-uploads/2bbaa664-89c2-45e5-80a8-fd888d8066c0.png"
                      alt={c.stairway.logoAlt}
                      className="h-8 md:h-10 drop-shadow-sm"
                    />
                  </div>

                  {/* Bars + arrow overlay */}
                  <div className="relative">
                    {/* Arrow SVG — sits on top of bars */}
                    <svg
                      className="absolute inset-0 w-full pointer-events-none z-30"
                      style={{ height: totalH + svgPad }}
                      viewBox={`0 0 600 ${totalH + svgPad}`}
                      preserveAspectRatio="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      {(() => {
                        const n = 6;
                        const gap = 600 / n; // 100 SVG units per bar slot

                        // Bar top y in SVG coords (no clearance — this is the actual bar top)
                        const barTopSVG = (i: number) => svgPad + totalH - (barBase + i * barStep);

                        // Clearance above the tallest bar (bar 6)
                        // Use a big fixed offset so the line floats well above all bars
                        const aboveTallest = 60; // SVG units above bar 6 top

                        // Anchor the line: passes through a point 60u above bar 6,
                        // and follows the same slope as the staircase
                        const x6 = gap * 5.5; // 550
                        const y6 = barTopSVG(6) - aboveTallest; // well above bar 6

                        const x1 = gap * 0.5;  // 50
                        // slope = barStep / gap = 52/100 (staircase rise per unit X)
                        const slope = -barStep / gap; // -0.52 (rising left→right means decreasing y)
                        const y1 = y6 - slope * (x6 - x1); // follow same slope back to bar 1

                        // Extend start left of bar 1 and end past bar 6
                        const startX = 0;
                        const startY = y1 + slope * (startX - x1);
                        const endX = 594;
                        const endY = y6 + slope * (endX - x6);

                        // Proper angled arrowhead
                        const angle = Math.atan2(endY - startY, endX - startX);
                        const aLen = 24;
                        const tip = { x: endX, y: endY };
                        const left = {
                          x: tip.x - aLen * Math.cos(angle - 0.38),
                          y: tip.y - aLen * Math.sin(angle - 0.38),
                        };
                        const right = {
                          x: tip.x - aLen * Math.cos(angle + 0.38),
                          y: tip.y - aLen * Math.sin(angle + 0.38),
                        };

                        return (
                          <>
                            <line
                              x1={startX} y1={startY}
                              x2={endX}   y2={endY}
                              stroke="#2A1E47"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                            />
                            <polygon
                              points={`${tip.x},${tip.y} ${left.x},${left.y} ${right.x},${right.y}`}
                              fill="#2A1E47"
                            />
                          </>
                        );
                      })()}
                    </svg>

                    {/* Bars row */}
                    <div
                      className="flex items-end justify-center gap-2 sm:gap-3 md:gap-5"
                      style={{ paddingTop: svgPad }}
                    >
                      {STEPS.map((step) => {
                        const height = barBase + step.level * barStep;
                        const isActive = activeStep === step.level;
                        return (
                          <button
                            key={step.level}
                            onClick={() => setActiveStep(isActive ? null : step.level)}
                            className={cn(
                              'relative z-20 group transition-all duration-300 cursor-pointer',
                              isActive ? 'scale-105' : 'hover:scale-[1.03]'
                            )}
                          >
                            <div
                              className={cn(
                                'w-16 sm:w-20 md:w-24 lg:w-28 rounded-t-lg transition-all duration-300',
                                isActive ? 'shadow-xl ring-2 ring-[#FF6B35]/40' : 'shadow-md group-hover:shadow-lg'
                              )}
                              style={{
                                height,
                                backgroundColor: isActive ? '#FF6B35' : step.color,
                              }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Labels row */}
                  <div className="flex justify-center gap-2 sm:gap-3 md:gap-5 mt-3">
                    {STEPS.map((step) => (
                      <div key={step.level} className="w-16 sm:w-20 md:w-24 lg:w-28 flex flex-col items-center">
                        <span className="text-sm md:text-base font-bold text-[#2A1E47]">{step.level}</span>
                        <span className="hidden md:block text-xs text-muted-foreground mt-1 text-center leading-tight h-8">
                          {step.subtitle}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Baseline */}
                  <div className="mt-4">
                    <div className="h-px bg-[#2A1E47]/15 mb-3" />
                    <p className="text-center text-sm text-muted-foreground">
                      {c.stairway.baseline}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Expanded detail card */}
            {activeStep !== null && (
              <div className="mt-12 max-w-3xl mx-auto animate-fade-in-up">
                {(() => {
                  const step = STEPS[activeStep - 1];
                  return (
                    <div className="rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
                      <div className="px-8 py-5 flex items-center gap-4" style={{ backgroundColor: step.color }}>
                        <span className="text-3xl font-bold text-white/30">0{step.level}</span>
                        <div>
                          <h3 className="text-xl font-bold text-white">{step.title}</h3>
                          <p className="text-white/70 text-sm">{step.subtitle}</p>
                        </div>
                      </div>
                      <div className="px-8 py-6 bg-white">
                        <p className="text-muted-foreground mb-5">{step.description}</p>
                        <div className="space-y-2">
                          {step.symptoms.map((s, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <span
                                className="flex-shrink-0 w-2 h-2 rounded-full mt-2"
                                style={{ backgroundColor: step.level === 6 ? '#FF6B35' : '#FFECD3', minWidth: 8, minHeight: 8 }}
                              />
                              <span className="text-sm text-[#2A1E47]/80">{s}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </section>

        {/* The journey narrative */}
        <section className="py-20 md:py-28 bg-[#FAFAFA]">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto reveal">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.journey.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47] mb-12">
                {c.journey.title}
              </h2>

              <div className="space-y-0">
                {STEPS.map((step, i) => (
                  <div key={step.level} className="relative pl-12 md:pl-16 pb-12 last:pb-0 reveal">
                    {/* Vertical line */}
                    {i < STEPS.length - 1 && (
                      <div className="absolute left-[19px] md:left-[23px] top-10 bottom-0 w-px bg-[#2A1E47]/10" />
                    )}
                    {/* Circle */}
                    <div
                      className="absolute left-0 top-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md"
                      style={{ backgroundColor: step.level === 6 ? '#FF6B35' : step.color }}
                    >
                      {step.level}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#2A1E47] mb-1">{step.title}</h3>
                      <p className="text-sm text-[#FF6B35] font-medium mb-3">{step.subtitle}</p>
                      <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF7F27] to-[#FF4500] rounded-3xl" />
          <div className="container-custom relative z-10">
            <div className="max-w-3xl mx-auto text-center pt-16 pb-8 px-4 reveal">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-tight">
                {c.cta.title}
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-10 max-w-xl mx-auto">
                {c.cta.text}
              </p>
              <div className="flex justify-center">
                <Link
                  to="/tell-me-more"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#2a1e48] hover:bg-[#2a1e48]/90 text-white text-base font-medium transition-all duration-300 shadow-lg border border-white/10"
                >
                  {c.cta.button}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* What we deliver per level */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2A1E47] mb-4">
              {c.deliver.title}
            </h2>
            <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto mb-16">
              {c.deliver.subtitle}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {c.deliver.cards.map((card, i) => (
                <div
                  key={card.level}
                  className="rounded-2xl border border-gray-100 bg-white p-7 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{DELIVER_ICONS[i]}</span>
                    <span className="text-xs font-bold uppercase tracking-widest text-[#FF6B35]">{c.deliver.levelPrefix} {card.level}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#2A1E47] mb-3">{card.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{card.what}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default OurWorkData;
